import { computed, inject, Injectable, signal } from '@angular/core';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { switchMapTo, tap } from 'rxjs/operators';

import type { IFooterSettings } from 'components/organisms/footer/entities/types';
import { TypicalProjectsRestService } from 'src/services/typical-projects-rest/typical-projects-rest.service';
import type { TFullColorScheme } from 'src/shared/types/color-schemes-and-themes';
import { EProductType } from 'src/shared/types/product-type';
import { PRODUCT_TYPE } from 'src/tokens/product-type/product-type';

import { LayoutDataService } from './layout-data.service';
import type { IDefaultLayout, ILandingPageLayoutMeta, IMenuItem, THeaderType } from '../entities/types';

const AVAILABLE_FOOTER_TYPES = ['full', 'light'] as const;

export type TAvailableFooterTypes = (typeof AVAILABLE_FOOTER_TYPES)[number];

@Injectable({ providedIn: 'root' })
export class LayoutService {
    private readonly restService = inject(TypicalProjectsRestService);
    private readonly layoutDataService = inject(LayoutDataService);
    private readonly productType = inject(PRODUCT_TYPE);

    private readonly footerVariantSignal = signal<TAvailableFooterTypes | null>(null);
    private readonly footerColorSchemeSignal = signal<TFullColorScheme>('midnight');
    private readonly isArticle = signal<boolean>(false);

    public readonly footerVariant = this.footerVariantSignal.asReadonly();
    public readonly footerColorScheme = this.footerColorSchemeSignal.asReadonly();
    public readonly footerData = this.layoutDataService.footerData;
    public readonly footerSecondaryData = this.layoutDataService.footerSecondaryData;
    public readonly isKlaxoonFooter = computed(() => this.productType === EProductType.Klaxoon);
    public readonly hasFooterSecondary = computed(() => (this.footerSecondaryData()?.length ?? 0) > 0);

    public setFooterData(data: IFooterSettings) {
        this.layoutDataService.setFooterData(data);
    }

    public getFooterData(): IFooterSettings | null {
        return this.layoutDataService.getFooterData();
    }

    public setFooterSecondaryData(data: IMenuItem[] | null): void {
        this.layoutDataService.setFooterSecondaryData(data);
    }

    public getFooterSecondaryData(): IMenuItem[] | null {
        return this.layoutDataService.getFooterSecondaryData();
    }

    public setHeaderData(data: IMenuItem[] | null) {
        this.layoutDataService.setHeaderData(data);
    }

    public getHeaderData(): IMenuItem[] | null {
        return this.layoutDataService.getHeaderData();
    }

    public setHeaderType(type: THeaderType | null): void {
        this.layoutDataService.setHeaderType(type);
    }

    public shouldRenderHeader(): boolean {
        return this.layoutDataService.getHeaderType() === 'full';
    }

    public setIsArticle(value: boolean): void {
        this.isArticle.set(value);
    }

    public getIsArticle(): boolean {
        return this.isArticle();
    }

    public configureFooterFromLayout(layout: ILandingPageLayoutMeta | undefined): void {
        if (!layout) {
            return;
        }

        if (layout.footer_settings) {
            this.setFooterData(layout.footer_settings);
        }

        this.setFooterSecondaryData(layout.footer_settings?.secondary_menu_data || null);
        this.configureFooter(layout.footer_type ?? 'full', layout.last_widget_color);
    }

    public configureFooter(variant: TAvailableFooterTypes, lastWidgetColor?: TFullColorScheme): void {
        if (AVAILABLE_FOOTER_TYPES.indexOf(variant) === -1) {
            throw new Error(
                `Invalid footer variant: ${variant}. Available variants: "${AVAILABLE_FOOTER_TYPES.join('", "')}"`
            );
        }

        this.footerVariantSignal.set(variant);
        this.footerColorSchemeSignal.set(this.resolveFooterColorScheme(lastWidgetColor));
    }

    public fetchDefaultLayoutOnError(): Observable<IDefaultLayout> {
        return this.restService.getDefaultPageLayout().pipe(
            tap((data) => {
                if (data?.main_navigation) {
                    this.layoutDataService.setHeaderType('full');
                    this.layoutDataService.setHeaderData(data.main_navigation);
                }
                if (data?.footer_menu) {
                    const currentFooterData = this.layoutDataService.getFooterData();
                    this.layoutDataService.setFooterData({ ...currentFooterData, menu_data: data.footer_menu });
                }
            }),
            switchMapTo(of(null))
        );
    }

    private resolveFooterColorScheme(lastWidgetColor?: TFullColorScheme): TFullColorScheme {
        if (lastWidgetColor === 'collaborate-blue') {
            return 'midnight';
        }

        if (lastWidgetColor === 'collaborate-white') {
            return 'white';
        }

        return lastWidgetColor ?? 'midnight';
    }
}
